import React, { useEffect, useState } from "react";
import { skills } from "../../data/constants";
import styled from "styled-components";
import LazyImage from "../LazyLoading/LazyImage";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import "swiper/css/pagination";
import { Pagination, Autoplay } from "swiper/modules";
import { TypographyH2, TypographyLead } from "../ui/Typography";

const Container = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  position: relative;
  z-index: 1;
  align-items: center;
`;

const Wrapper = styled.div`
  position: relative;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-direction: column;
  width: 100%;
  max-width: 1100px;
  gap: 12px;
  margin-bottom: -90px; /* Decreased bottom margin */
  @media (max-width: 960px) {
    flex-direction: column;
  }
`;

export const Title = styled(TypographyH2)`
  font-size: 42px;
  text-align: center;
  margin-top: 20px;
  color: ${({ theme }) => theme.text_primary};
  border-bottom: none; /* Override TypographyH2 border if any */
  @media (max-width: 768px) {
    margin-top: 12px;
    font-size: 32px;
  }
`;

export const Desc = styled(TypographyLead)`
  text-align: center;
  max-width: 600px;
  @media (max-width: 768px) {
    font-size: 16px;
    padding: 0 30px;
  }
`;

const SkillsContainer = styled.div`
  width: 100%;
  display: flex;
  flex-wrap: wrap;
  margin-top: 30px;
  gap: 30px;
  justify-content: center;
`;

const Skill = styled.div`
  width: 100%;
  max-width: 500px;
  background: linear-gradient(135deg, rgba(3, 7, 18, 0.95) 0%, rgba(3, 7, 18) 100%);
  border: 1.5px solid rgba(59, 130, 246, 0.36);
  box-shadow: 0 0 20px rgba(59, 130, 246, 0.36);
  border-radius: 16px;
  padding: 18px 36px;
  pointer-events: auto;
  @media (max-width: 768px) {
    max-width: 400px;
    padding: 10px 36px;
  }
  @media (max-width: 500px) {
    max-width: 330px;
    padding: 10px 36px;
  }
`;

const SkillTitle = styled.h2`
  font-size: 28px;
  font-weight: 600;
  color: ${({ theme }) => theme.text_secondary};
  margin-bottom: 20px;
  text-align: center;
`;

const SkillList = styled.div`
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 20px;
`;

const SkillItem = styled.div`
  font-size: 16px;
  font-weight: 400;
  color: ${({ theme }) => theme.text_primary + 80};
  border: 1px solid rgba(59, 130, 246, 0.46);
  border-radius: 12px;
  padding: 12px 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  @media (max-width: 768px) {
    font-size: 14px;
    padding: 8px 12px;
  }
  @media (max-width: 500px) {
    font-size: 14px;
    padding: 6px 12px;
  }
`;

export const SwiperMobileCenter = styled.div`
  @media (max-width: 768px) {
    width: 100%;
    .swiper-slide {
      display: flex;
      justify-content: center;
      align-items: center;
    }
  }
`;

const Skills = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return (
    <Container id="skills">
      <Wrapper>
        <Title>Skills</Title>
        <Desc>Here are some of my skills on which I have been working on.</Desc>
        {isMobile ? (
          <SwiperMobileCenter>
            <Swiper
              spaceBetween={30}
              slidesPerView={1}
              loop={true}
              autoplay={{ delay: 2000, disableOnInteraction: false, pauseOnMouseEnter: true }}
              modules={[Pagination, Autoplay]}
              style={{ width: '100%', padding: '20px' }}
            >
              {skills.map((skill, index) => (
                <SwiperSlide key={index}>
                  <Skill>
                    <SkillTitle>{skill.title}</SkillTitle>
                    <SkillList>
                      {skill.skills.map((item, idx) => (
                        <SkillItem key={idx}>
                          <LazyImage src={item.image} alt={item.name} />
                          {item.name}
                        </SkillItem>
                      ))}
                    </SkillList>
                  </Skill>
                </SwiperSlide>
              ))}
            </Swiper>
          </SwiperMobileCenter>
        ) : (
          <SkillsContainer>
            {skills.map((skill) => (
              <Skill key={skill.title}>
                <SkillTitle>{skill.title}</SkillTitle>
                <SkillList>
                  {skill.skills.map((item) => (
                    <SkillItem key={item.name}>
                      <LazyImage src={item.image} alt={item.name} />
                      {item.name}
                    </SkillItem>
                  ))}
                </SkillList>
              </Skill>
            ))}
          </SkillsContainer>
        )}
      </Wrapper>
    </Container>
  );
};

export default Skills;

